import { useCallback } from 'react';
import styled from '@emotion/styled';
import { Button, Modal, Stack, tokens } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import { useOwnedAccount } from '@hotjar/account';
import {
  createGlobalModal,
  useEffectOnceWithDependencies,
} from '../../external-dependencies/webapp-utils';
import type { GlobalModalProps } from '../../external-dependencies/webapp-utils';
import { ChurnDeflection } from './components';
import { useProductsToDowngrade } from './hooks/useProductsToDowngrade';
import { useDailySessions } from './hooks/useDailySessions';
import { extractSiteIdFromProducts } from './utils/extractSiteIdFromProducts';
import type { Currency, BillingPeriod, ProductAllowance } from './types';
import {
  trackComparePlansClick,
  trackContinueToDowngradeClick,
  trackKeepPlansClick,
  trackMakeEnquiryClick,
  trackModalClosed,
  trackModalLoaded,
} from './tracking';

const ModalBody = styled(Modal.Body)`
  display: flex;
  flex-grow: 1;
  align-items: center;
  padding: ${tokens.spacer.x3};
`;

const ModalBodyInner = styled(Stack)`
  margin: auto;
  display: flex;
  flex-direction: column;
  min-width: 800px;
  gap: ${tokens.spacer.x3};
`;

const ModalFooter = styled(Modal.Footer)`
  border-top: 1px solid ${tokens.color.neutral_200};
`;

interface Props {
  currentProducts: ProductAllowance[];
  subscriptionEndDate: string;
  billingPeriod: BillingPeriod;
  currency: Currency;
}

export function ChurnDeflectionModal({
  currentProducts,
  subscriptionEndDate,
  billingPeriod,
  currency,
  onSubmit,
  onCancel: onClose,
}: Props & GlobalModalProps<void, void>): JSX.Element | null {
  const { t } = useTranslation('voluntary-churn');

  const siteId = extractSiteIdFromProducts(currentProducts);
  const { data: dailySessions = 0 } = useDailySessions(siteId);
  const { data: account } = useOwnedAccount();
  const productsToDowngrade = useProductsToDowngrade(currentProducts, {
    billingPeriod,
    currency,
  });

  useEffectOnceWithDependencies(
    () => {
      trackModalLoaded(account);
    },
    [account],
    () => account !== undefined,
  );

  const handleClose = useCallback(() => {
    trackModalClosed(account);
    onClose();
  }, [account, onClose]);

  const handleKeepPlans = useCallback(() => {
    trackKeepPlansClick(account);
    onClose();
  }, [account, onClose]);

  const handleSubmit = useCallback(() => {
    trackContinueToDowngradeClick(account);
    onSubmit();
  }, [account, onSubmit]);

  const handleComparePlansClick = useCallback(() => {
    trackComparePlansClick(account);
  }, [account]);

  const handleMakeEnquiryClick = useCallback(() => {
    trackMakeEnquiryClick(account);
  }, [account]);

  if (productsToDowngrade == null) {
    return null;
  }

  const { observe, ask } = productsToDowngrade;

  return (
    <Modal
      isOpen
      contentLabel={t('churn.deflection-modal.title')}
      size="fullscreen"
      onClose={handleClose}
      appElementSelector="#app-container"
    >
      <ModalBody>
        <ModalBodyInner>
          <ChurnDeflection
            observeProduct={observe}
            askProduct={ask}
            dailySessions={dailySessions}
            subscriptionEndDate={subscriptionEndDate}
            onComparePlansClick={handleComparePlansClick}
            onMakeAnEnquiryClick={handleMakeEnquiryClick}
          />
        </ModalBodyInner>
      </ModalBody>

      <ModalFooter>
        <Button kind="danger" onClick={handleSubmit}>
          {t('churn.deflection-modal.continue-to-downgrade')}
        </Button>
        <Button kind="ghost" onClick={handleKeepPlans}>
          {t('churn.deflection-modal.keep-plans')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export const showChurnDeflectionModal = createGlobalModal<Props, void, void>(ChurnDeflectionModal);
